'use client';

import { useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useLogin } from '@/modules/auth/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { useCart } from '@/modules/cart/context/cart-context';
import { useCheckout } from '../context/checkout-context';
import { apiClient } from '@/lib/api-client';
import { TAX_RATE } from '@/config/constants';
import { useAuth } from '@clerk/nextjs';

export function useCheckoutAuth() {
  const [isLoading, setIsLoading] = useState(false);
  const isCreatingRef = useRef(false);
  
  const router = useRouter();
  const { isSignedIn, getToken } = useAuth();
  const login = useLogin({ skipRedirect: true });
  const { toast } = useToast();
  const { items, clearCart } = useCart();
  const { paymentMethod, shippingAddress, guestEmail, setGuestEmail } = useCheckout();

  const findExistingPendingOrderId = async (): Promise<string | undefined> => {
    try {
      const token = await getToken();
      const { data } = await apiClient.get('/orders/myorders', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const now = Date.now();
      const existing = (data as any[]).find(o => !o.isPaid && !o.isCancelled && (!o.holdExpiresAt || new Date(o.holdExpiresAt).getTime() > now));
      return existing?._id as string | undefined;
    } catch {
      return undefined;
    }
  };

  const createOrderForAuthenticatedUser = async () => {
    try {
      if (isCreatingRef.current) return;
      isCreatingRef.current = true;
      if (!items || items.length === 0) {
        toast({ title: 'Your cart is empty', description: 'Add items to cart first.' });
        return;
      }

      // If there's an existing pending order with a valid hold, reuse it
      const existingId = await findExistingPendingOrderId();
      if (existingId) {
        // Before reusing the order, sync items and totals with current cart
        const token = await getToken();
        const orderItems = items.map(item => ({
          name: item.name,
          qty: item.qty,
          image: item.image,
          price: item.price,
          productId: item.productId,
        }));
        try {
          await apiClient.put(`/orders/${existingId}/sync`, { orderItems, shippingDetails: shippingAddress ?? undefined }, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          router.push(`/orders/${existingId}`);
          return;
        } catch {
          // If sync fails (e.g., hold expired), proceed to create a new order
        }
      }

      const orderItems = items.map(item => ({
        name: item.name,
        qty: item.qty,
        image: item.image,
        price: item.price,
        productId: item.productId,
      }));

      const itemsPrice = items.reduce((acc, item) => acc + item.price * item.qty, 0);
      const allCoins = items.length > 0 && items.every(it => {
        const sub = (it as any).subCategory as string | undefined;
        const cat = (it as any).category as string | undefined;
        const s1 = (sub || '').trim().toLowerCase();
        const s2 = (cat || '').trim().toLowerCase();
        return s1 === 'coin' || s1 === 'coins' || s2 === 'coin' || s2 === 'coins';
      });
      const totalCardsQty = items.reduce((acc, it) => {
        const sub = (it as any).subCategory as string | undefined;
        const cat = (it as any).category as string | undefined;
        const s1 = (sub || '').trim().toLowerCase();
        const s2 = (cat || '').trim().toLowerCase();
        const isCard = s1 === 'card' || s1 === 'cards' || s2 === 'card' || s2 === 'cards';
        return acc + (isCard ? it.qty : 0);
      }, 0);
      const shippingPrice = allCoins
        ? 0
        : totalCardsQty >= 5
          ? 9
          : totalCardsQty > 0
            ? 3.5
            : itemsPrice > 100
              ? 0
              : 10;
      const taxPrice = Number((itemsPrice * TAX_RATE).toFixed(2));
      const totalPrice = Number((itemsPrice + shippingPrice + taxPrice).toFixed(2));

      const body = {
        orderItems,
        paymentMethod: paymentMethod ?? 'Stripe',
        itemsPrice,
        taxPrice,
        shippingPrice,
        totalPrice,
        shippingDetails: shippingAddress ?? undefined,
      };

      // Attach Clerk session token so the backend Clerk guard authenticates the request
      const token = await getToken();
      const { data } = await apiClient.post('/orders', body, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      router.push(`/orders/${data._id}`);
    } catch (error) {
      toast({ title: 'Error creating order', description: 'Please try again.', variant: 'destructive' });
    } finally {
      isCreatingRef.current = false;
    }
  };

  const handleGuestCheckout = async () => {
    try {
      if (!items || items.length === 0) {
        toast({ title: 'Your cart is empty', description: 'Add items to cart first.' });
        return;
      }

      const orderItems = items.map(item => ({
        name: item.name,
        qty: item.qty,
        image: item.image,
        price: item.price,
        productId: item.productId,
      }));

      // Minimal totals; let server validate/tax as needed. Client computes to show UI normally.
      const itemsPrice = items.reduce((acc, item) => acc + item.price * item.qty, 0);
      const allCoins = items.length > 0 && items.every(it => {
        const sub = (it as any).subCategory as string | undefined;
        const cat = (it as any).category as string | undefined;
        const s1 = (sub || '').trim().toLowerCase();
        const s2 = (cat || '').trim().toLowerCase();
        return s1 === 'coin' || s1 === 'coins' || s2 === 'coin' || s2 === 'coins';
      });
      const totalCardsQty = items.reduce((acc, it) => {
        const sub = (it as any).subCategory as string | undefined;
        const cat = (it as any).category as string | undefined;
        const s1 = (sub || '').trim().toLowerCase();
        const s2 = (cat || '').trim().toLowerCase();
        const isCard = s1 === 'card' || s1 === 'cards' || s2 === 'card' || s2 === 'cards';
        return acc + (isCard ? it.qty : 0);
      }, 0);
      const shippingPrice = allCoins
        ? 0
        : totalCardsQty >= 5
          ? 9
          : totalCardsQty > 0
            ? 3.5
            : itemsPrice > 100
              ? 0
              : 10;
      const taxPrice = Number((itemsPrice * TAX_RATE).toFixed(2));
      const totalPrice = Number((itemsPrice + shippingPrice + taxPrice).toFixed(2));

      const body = {
        orderItems,
        paymentMethod: paymentMethod ?? 'Stripe',
        itemsPrice,
        taxPrice,
        shippingPrice,
        totalPrice,
        shippingDetails: shippingAddress ?? undefined,
        guestEmail,
      };

      const { data } = await apiClient.post('/orders/guest', body);
      router.push(`/orders/${data._id}`);
    } catch (error) {
      toast({ title: 'Error starting checkout', description: 'Please try again.', variant: 'destructive' });
    }
  };

  const handleSignIn = async (email: string, password: string) => {
    if (!email || !password) {
      toast({
        title: 'Missing information',
        description: 'Please enter both email and password.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      await login.mutateAsync({ email, password });
      // Do not create or sync any order on sign-in; user remains on cart
      return 'signed_in';
    } catch (error) {
      // Error handling is done in the login hook
    } finally {
      setIsLoading(false);
    }
  };

  const proceedToCheckout = async () => {
    if (isSignedIn) {
      // User is already authenticated, create order directly
      await createOrderForAuthenticatedUser();
    } else {
      // User is not authenticated, show auth options
      // This will be handled by the UI component
      return 'show_auth_options';
    }
  };

  return {
    // Maintain existing shape for callers; presence indicates auth
    user: isSignedIn ? ({ id: 'clerk' } as any) : null,
    isLoading,
    proceedToCheckout,
    handleGuestCheckout,
    handleSignIn,
    setGuestEmail,
  };
}
